/**
 * Vista Carichi di lavoro — bimestre selezionato (Circolare ISS n. 6/2026),
 * settimane ISO del bimestre e totale anno.
 */

import {
  getState,
  setWorkloadBimester,
  setWorkloadAnnualYear,
  getBimesterSnapshots,
  saveBimesterSnapshot,
  deleteBimesterSnapshot,
  canExportWorkloadBimesterPdf,
} from '../store.js';
import {
  computeWeeklyBreakdownForWeekRange,
  getWeekStartsInRange,
  computeAnnualWorkloadFromAssignments,
  computeBimesterMirror,
  getBimesterForDate,
  getBimesterRange,
  getAdjacentBimester,
  parseBimesterKey,
  countFerialiInDateRange,
  findWorkloadGaps,
  doctorsForWorkloadBalance,
  isExcludedFromWorkloadBalance,
} from '../workload.js';
import { HOURS_PER_SLOT, HOURS_PER_DAY } from '../types.js';
import { getISOWeekString, getWeekStart, formatLocalISODate, hoursPaceBarHTML, PDF_DOWNLOAD_ICON_HTML } from '../utils.js';
import { exportBimesterWorkloadPdf } from '../pdfExport.js';

const MONTH_NAMES_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

/** Le sei coppie di mesi del bimestre, per il selettore. */
const BIMESTER_OPTIONS = [1, 2, 3, 4, 5, 6].map(i => ({
  index: i,
  label: `${MONTH_NAMES_FULL[(i - 1) * 2]}–${MONTH_NAMES_FULL[(i - 1) * 2 + 1]}`,
}));

/** Ore con al massimo una cifra decimale (es. 12.0h). */
function fmtHours(n) {
  const x = Number(n) || 0;
  return (Math.round(x * 10) / 10).toFixed(1);
}

/** Ore con segno esplicito (es. "+1.2h", "-2.5h", "0.0h"). */
function fmtSignedHours(n) {
  const x = Math.round((Number(n) || 0) * 10) / 10;
  return `${x > 0 ? '+' : ''}${x.toFixed(1)}h`;
}

/** Slot equivalenti con segno (1 slot ≈ 6h). */
function fmtSignedSlots(hours) {
  const s = Math.round(((Number(hours) || 0) / HOURS_PER_SLOT) * 10) / 10;
  return `${s > 0 ? '+' : ''}${s.toFixed(1)} slot`;
}

/** Badge saldo: ore sopra, slot equivalenti sotto (1 slot ≈ 6h). */
function balanceBadgeHtml(diffHours, title = '') {
  return `<span class="wl-badge ${balanceClass(diffHours)}"${title ? ` title="${escapeHtmlText(title)}"` : ''}>`
    + `<span class="wl-badge-main">${fmtSignedHours(diffHours)}</span>`
    + `<span class="wl-badge-sub">${fmtSignedSlots(diffHours)}</span></span>`;
}

/** Classe colore per un saldo (tolleranza ±0.05h). */
function balanceClass(diff) {
  if (diff < -0.05) return 'neg';
  if (diff > 0.05) return 'pos';
  return 'ok';
}

/** Data breve "1 set" oppure "31 ott 2026". */
function fmtDayMonth(dateStr, withYear = false) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('it-IT', withYear
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short' });
}

/** Data/ora leggibile per la riga di consolidamento. */
function fmtConsolidationTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).endsWith('Z') || String(iso).includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Escape minimale per testo interpolato nell'HTML. */
function escapeHtmlText(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}

/** Riga di stato del consolidamento sotto la tabella del bimestre. */
function renderBimesterConsolidationHtml({ bimesterSnapshot, snapshotDrift, canConsolidate, bimesterGaps }) {
  const gapWarning = (bimesterGaps?.totalWeeks || bimesterGaps?.totalPartialWeeks)
    ? `<p class="negative wl-consolidation-line">Consolidare adesso registrerebbe saldi calcolati su dati ${bimesterGaps.totalWeeks ? 'incompleti' : 'forse incompleti'}.</p>`
    : '';
  const actions = canConsolidate
    ? `<div class="wl-consolidation-actions">
         <button type="button" class="btn btn-secondary btn-sm" data-action="bimester-consolidate">${bimesterSnapshot ? 'Aggiorna consolidamento' : 'Consolida bimestre'}</button>
         ${bimesterSnapshot ? '<button type="button" class="btn btn-danger btn-sm" data-action="bimester-unconsolidate">Rimuovi consolidamento</button>' : ''}
       </div>`
    : '';

  if (!bimesterSnapshot) {
    return `<div class="wl-consolidation">
        <p class="wl-consolidation-line">Saldi <strong>non congelati</strong>: senza consolidamento un saldo contestato più avanti non è più ricostruibile, perché il piano nel frattempo cambia.</p>
        ${gapWarning}
        ${actions}
      </div>`;
  }

  const by = bimesterSnapshot.createdBy ? ` da <strong>${escapeHtmlText(bimesterSnapshot.createdBy)}</strong>` : '';
  const drift = snapshotDrift.length
    ? `<p class="wl-consolidation-line"><span class="negative">Il piano è cambiato dopo il consolidamento per ${snapshotDrift.length} ${snapshotDrift.length === 1 ? 'medico' : 'medici'}:</span>
       ${snapshotDrift.map(x => `${escapeHtmlText(x.sigla)} (${fmtSignedHours(x.before)} → ${fmtSignedHours(x.after)})`).join(', ')}.
       In tabella ci sono i valori attuali; quelli consolidati restano registrati.</p>`
    : '';

  return `<div class="wl-consolidation">
      <p class="wl-consolidation-line">Consolidato il ${escapeHtmlText(fmtConsolidationTimestamp(bimesterSnapshot.createdAt))}${by}.</p>
      ${drift}
      ${gapWarning}
      ${actions}
    </div>`;
}

/** Domenica della settimana ISO che inizia il lunedì indicato. */
function getWeekEndForRange(weekStart) {
  const d = new Date(weekStart + 'T12:00:00');
  d.setDate(d.getDate() + 6);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Riga di riepilogo dei buchi di registrazione, con i medici e quante settimane. */
function renderGapsSummaryHtml(gaps, doctors, contesto) {
  if (!gaps) return '';
  const listaSigle = (mappa) => doctors
    .filter(d => mappa[d.id]?.length)
    .map(d => `${escapeHtmlText(d.sigla)} (${mappa[d.id].length})`);

  const interi = gaps.totalWeeks > 0 ? listaSigle(gaps.byDoctor) : [];
  const parziali = gaps.totalPartialWeeks > 0 ? listaSigle(gaps.partialByDoctor ?? {}) : [];
  if (interi.length === 0 && parziali.length === 0) return '';

  const rigaInteri = interi.length
    ? `<p class="negative wl-gap-line">
        ⚠ Dato incompleto ${escapeHtmlText(contesto)}: ${interi.join(', ')} ${interi.length === 1 ? 'ha' : 'hanno'} settimane senza turni né assenze registrate.
        Le ore dovute risultano più alte del reale finché l'assenza non viene inserita.
      </p>`
    : '';
  const rigaParziali = parziali.length
    ? `<p class="wl-gap-line wl-muted">
        ? Da verificare ${escapeHtmlText(contesto)}: ${parziali.join(', ')} ${parziali.length === 1 ? 'ha' : 'hanno'} settimane sotto la metà del monte orario senza assenze registrate.
        Di solito è un'assenza parziale non inserita.
      </p>`
    : '';
  return rigaInteri + rigaParziali;
}

function workloadYearOptions() {
  const cy = new Date().getFullYear();
  const years = [];
  for (let y = cy - 5; y <= cy + 2; y++) years.push(y);
  return years;
}

/** Riquadro numerico in testa alla card bimestre. */
function statTileHtml({ label, value, hint, valueClass = '' }) {
  return `<div class="wl-stat">
      <div class="wl-stat-label">${label}</div>
      <div class="wl-stat-value ${valueClass}">${value}</div>
      ${hint ? `<div class="wl-stat-hint">${hint}</div>` : ''}
    </div>`;
}

export function renderWorkloadView(container) {
  const state = getState();
  const allDoctors = state.doctors ?? [];
  const doctors = (state.currentUser?.role === 'user' && state.currentUser?.doctorId)
    ? allDoctors.filter(d => d.id === state.currentUser.doctorId)
    : allDoctors;
  // Il Direttore fa solo turni Direzione, sempre gli stessi: nei conti sul monte orario non c'e'.
  const balanceDoctors = doctorsForWorkloadBalance(doctors);
  const annualYear = state.workloadAnnualYear ?? new Date().getFullYear();
  const yearOpts = workloadYearOptions();

  // Il bimestre è l'unità di misura del monte orario (Circolare ISS n. 6/2026): è lui a guidare la pagina.
  const todayBimester = getBimesterForDate(formatLocalISODate(new Date()));
  const stored = parseBimesterKey(state.workloadBimester);
  const selected = stored ? getBimesterRange(stored.year, stored.index) : todayBimester;
  const isCurrentBimester = selected.key === todayBimester.key;

  const bimesterMirror = computeBimesterMirror(selected.year, selected.index, balanceDoctors);
  const range = bimesterMirror.range;
  const bimesterFerialiCount = countFerialiInDateRange(range.start, range.end);
  const bimesterFullTimeHours = bimesterFerialiCount * HOURS_PER_DAY;

  // Settimane ISO che intersecano il bimestre: lun–dom interi, anche a cavallo dei bordi.
  const weekFrom = getWeekStart(range.start);
  const weekTo = getWeekStart(range.end);
  const weeklyBreakdown = computeWeeklyBreakdownForWeekRange(weekFrom, weekTo);
  const weekStartsSorted = getWeekStartsInRange(weekFrom, weekTo);

  // Settimane senza turni né assenze: quasi sempre assenze non registrate. Gonfiano le ore dovute,
  // quindi falsano il saldo e, a valle, il consolidamento su cui si fondano le ritenute.
  const bimesterGaps = findWorkloadGaps(range.start, range.end, balanceDoctors);
  const weeksGaps = findWorkloadGaps(weekFrom, getWeekEndForRange(weekTo), doctors);

  // Consolidamento: fotografia del bimestre alla chiusura, da cui dipendono le eventuali ritenute.
  const bimesterKey = range.key;
  const bimesterSnapshot = getBimesterSnapshots()[bimesterKey] || null;
  const canConsolidate = ['admin', 'director'].includes(state.currentUser?.role || '');
  const canExportPdf = Boolean(canExportWorkloadBimesterPdf());
  // Se il piano è cambiato dopo il consolidamento, il saldo vivo non coincide più con quello congelato.
  const snapshotDrift = bimesterSnapshot
    ? balanceDoctors.reduce((acc, d) => {
        const live = bimesterMirror.byDoctor[d.id];
        const frozen = bimesterSnapshot.data?.[d.id];
        if (!live || !frozen) return acc;
        const before = Number(frozen.saldo) || 0;
        if (Math.abs(before - live.saldo) > 0.05) acc.push({ sigla: d.sigla, before, after: live.saldo });
        return acc;
      }, [])
    : [];

  // Traiettoria: allineamento alla data fino a cui i turni sono stati prodotti. Sta solo sulla riga
  // del singolo medico — sommarlo tra medici mescolerebbe chi è avanti con chi è indietro.
  const plannedThrough = bimesterMirror.plannedThrough;
  const planLabel = plannedThrough ? fmtDayMonth(plannedThrough) : '';

  // Unico riquadro rimasto: è stato del bimestre, non un totale di reparto. Ore dovute, coperte e
  // saldo stanno sulla riga di ciascun medico, dove sono leggibili.
  const statsHtml = statTileHtml({
    label: 'Consolidamento',
    value: bimesterSnapshot ? '<span class="wl-badge pos">Consolidato</span>' : '<span class="wl-badge">Aperto</span>',
    hint: bimesterSnapshot
      ? `congelato il ${escapeHtmlText(fmtConsolidationTimestamp(bimesterSnapshot.createdAt)).split(',')[0]}`
      : 'saldi ancora ricalcolati in tempo reale',
  });

  container.innerHTML = `<div class="workload-view-root">
    <div class="card wl-toolbar-card">
      <div class="wl-toolbar">
        <div>
          <div class="card-title wl-toolbar-title">Carichi di lavoro</div>
          <div class="wl-toolbar-meta">
            ${escapeHtmlText(range.label)} · ${escapeHtmlText(fmtDayMonth(range.start))} – ${escapeHtmlText(fmtDayMonth(range.end, true))}
            ${isCurrentBimester ? '<span class="wl-chip">in corso</span>' : ''}
          </div>
        </div>
        <div class="wl-picker">
          <button type="button" class="wl-nav-btn" data-action="bimester-prev" title="Bimestre precedente" aria-label="Bimestre precedente">‹</button>
          <div class="wl-picker-fields">
            <div>
              <label for="workload-bimester">Bimestre</label>
              <select id="workload-bimester" title="Seleziona bimestre">
                ${BIMESTER_OPTIONS.map(o => `<option value="${o.index}" ${o.index === range.index ? 'selected' : ''}>${o.label}</option>`).join('')}
              </select>
            </div>
            <div>
              <label for="workload-bimester-year">Anno</label>
              <select id="workload-bimester-year" class="year-select" title="Seleziona anno">
                ${yearOpts.map(yr => `<option value="${yr}" ${yr === range.year ? 'selected' : ''}>${yr}</option>`).join('')}
              </select>
            </div>
          </div>
          <button type="button" class="wl-nav-btn" data-action="bimester-next" title="Bimestre successivo" aria-label="Bimestre successivo">›</button>
          ${isCurrentBimester ? '' : '<button type="button" class="btn btn-secondary btn-sm wl-today-btn" data-action="bimester-today">Oggi</button>'}
        </div>
      </div>
    </div>

    <div class="card">
      <div class="wl-card-head">
        <div>
          <div class="card-title">Bimestre ${escapeHtmlText(range.label)}</div>
          <div class="wl-card-sub">38h settimanali su base bimestrale — Circolare ISS n. 6 del 3/7/2026${plannedThrough ? ` · turni prodotti fino al ${escapeHtmlText(planLabel)}` : ''}</div>
        </div>
        ${canExportPdf ? `<button type="button" class="btn btn-pdf btn-sm" data-action="bimester-pdf" title="Resoconto PDF del bimestre mostrato: ore dovute, coperte, riporto, allineamento a oggi e saldo per medico">${PDF_DOWNLOAD_ICON_HTML}<span class="btn-pdf-label">PDF bimestre</span></button>` : ''}
      </div>
      <div class="wl-stats">${statsHtml}</div>
      <div class="table-wrapper">
        <table class="workload-table wl-sticky-first">
            <thead>
              <tr>
                <th>Medico</th>
                <th class="num">Ore dovute</th>
                <th class="num" title="Ore coperte nel bimestre. La barra è solo sullo stato a oggi: pallino = dove sei, asta = dove dovresti essere${plannedThrough ? ` al ${escapeHtmlText(planLabel)}` : ''}.">Coperte</th>
                <th class="num">Riporto</th>
                <th class="num" title="Ore coperte + riporto − ore dovute fino a dove i turni sono stati prodotti${plannedThrough ? ` (${escapeHtmlText(planLabel)})` : ''}. Non conta le settimane ancora da redigere.">A oggi</th>
                <th class="num" title="Ore coperte + riporto − ore dovute sull'intero bimestre: quanto manca per chiuderlo in pari.">Saldo bimestre</th>
              </tr>
            </thead>
            <tbody>
              ${balanceDoctors.map(d => {
                const b = bimesterMirror.byDoctor[d.id];
                if (!b) return '';
                // La barra parla solo dello stato a oggi: dove sei rispetto a dove dovresti essere
                // alla data dei turni prodotti. Il totale del bimestre resta un numero, senza barra.
                const suPiano = Boolean(plannedThrough) && b.dovuteAlPiano > 0;
                const pct = suPiano ? Math.max(0, Math.round((b.coperteAlPiano / b.dovuteAlPiano) * 100)) : 0;
                // Il pallino sta sulle ore coperte fino alla data dei turni prodotti, come l'asta:
                // le ore gia' coperte dopo (ferie registrate in anticipo) vanno nel tratto chiaro.
                const coperteFuture = Math.max(0, b.coperte - b.coperteAlPiano);
                const barTitle = suPiano
                  ? `Sei a ${fmtHours(b.coperteAlPiano)}h; a oggi dovresti essere a ${fmtHours(b.dovuteAlPiano)}h (turni fino al ${planLabel})`
                    + (coperteFuture > 0.05 ? `; altre ${fmtHours(coperteFuture)}h gia' coperte dopo il ${planLabel}` : '')
                    + `; il bimestre chiude a ${fmtHours(b.dovute)}h`
                  : '';
                const barHtml = suPiano
                  ? hoursPaceBarHTML({
                      coperteAOggi: b.coperteAlPiano,
                      coperteFuture,
                      dovuteAOggi: b.dovuteAlPiano,
                      dovuteTotali: b.dovute,
                      label: barTitle,
                    })
                  : '';
                const esitoTraiettoria = b.scostamentoAlPiano < -0.05
                  ? 'sotto traiettoria'
                  : (b.scostamentoAlPiano > 0.05 ? 'sopra traiettoria' : 'in linea');
                const scostamentoCell = plannedThrough
                  ? balanceBadgeHtml(b.scostamentoAlPiano, `${esitoTraiettoria}: coperte + riporto − dovute fino al ${planLabel}. 1 slot ≈ ${HOURS_PER_SLOT}h.`)
                  : '<span class="wl-sub">—</span>';
                const turniTitle = `Consolidate (settimane archiviate): ${fmtHours(b.turniConsolidati)}h · provvisorie: ${fmtHours(b.turniProvvisori)}h`;
                const riporto = Math.abs(b.saldoApertura) > 0.05 ? fmtSignedHours(b.saldoApertura) : '—';
                const esito = b.oreMancanti > 0.05
                  ? `${fmtHours(b.oreMancanti)}h da assegnare`
                  : (b.oreEsubero > 0.05 ? 'in esubero' : 'in pari');
                return `
                    <tr>
                      <td class="wl-doc"><strong>${escapeHtmlText(d.sigla)}</strong> ${escapeHtmlText(d.cognome)}</td>
                      <td class="num" title="Somma sui lun–ven non festivi del bimestre: ore/giorno = debito settimanale ÷ 5.">${fmtHours(b.dovute)}h</td>
                      <td class="num">
                        <div>${fmtHours(b.coperte)}h ${suPiano ? `<span class="wl-sub" title="${escapeHtmlText(barTitle)}">(${pct}% a oggi)</span>` : ''}</div>
                        ${barHtml}
                        <div class="wl-sub" title="${turniTitle}">${fmtHours(b.turni)}h turni · ${fmtHours(b.assenze)}h assenze</div>
                      </td>
                      <td class="num" title="Riporto di apertura impostato in anagrafica per questo bimestre.">${riporto}</td>
                      <td class="num" title="Ore coperte + riporto − ore dovute fino al ${escapeHtmlText(planLabel || 'termine dei turni prodotti')}: negativo = indietro rispetto alla traiettoria.">${scostamentoCell}</td>
                      <td class="num">
                        ${balanceBadgeHtml(b.saldo, `${esito}: ore coperte + riporto − ore dovute sull'intero bimestre. 1 slot ≈ ${HOURS_PER_SLOT}h.`)}
                      </td>
                    </tr>
                  `;
              }).join('')}
            </tbody>
          </table>
        </div>
        ${renderGapsSummaryHtml(bimesterGaps, balanceDoctors, 'nel bimestre')}
        ${renderBimesterConsolidationHtml({ bimesterSnapshot, snapshotDrift, canConsolidate, bimesterGaps })}
        <details class="wl-note">
          <summary>Come si calcola il bimestre</summary>
          <p><strong>Ore dovute</strong> = debito settimanale ÷ 5 per ogni lun–ven non festivo del bimestre (${bimesterFerialiCount} giorni feriali → ${fmtHours(bimesterFullTimeHours)}h a tempo pieno).</p>
          <p><strong>Coperte</strong> = turni assegnati + assenze accreditate (congedo ordinario, permessi, rischio radiologico, malattia, formazione). Le reperibilità restano fuori dal monte orario.</p>
          <p><strong>A oggi</strong> = stesso calcolo del saldo ma fermato alla fine dell'ultima settimana con turni assegnati${plannedThrough ? ` (${escapeHtmlText(planLabel)})` : ''}: dice se il medico è allineato <em>adesso</em>, mentre il <strong>saldo bimestre</strong> conta anche le settimane ancora da redigere e resta negativo finché il piano non è completo. La barra sotto le ore coperte dice la stessa cosa in figura: il <strong>pallino</strong> è dove sei, l'<strong>asta</strong> è dove dovresti essere a quella data, e il <strong>tratto chiaro</strong> dopo il pallino sono le ore già coperte oltre quella data (per esempio ferie già registrate). Il totale del bimestre non ha barra: fino all'ultima settimana redatta sarebbe corta per tutti.</p>
          <p>La notte 20-8 conta 4h sul giorno di inizio e 8h sul successivo: a cavallo del bimestre entra solo la quota di competenza. Lo slot è approssimato come ore ÷ ${HOURS_PER_SLOT}h.</p>
          <p>Un debito residuo a fine bimestre, se non coperto da congedo o permessi, comporta ritenute sulla busta paga.</p>
          <p>Il <strong>Direttore</strong> non compare in questa tabella: fa solo turni Direzione, sempre gli stessi, quindi un saldo rispetto al debito settimanale non direbbe nulla. Per lo stesso motivo il solver lo tiene fuori dai bilanciamenti.</p>
        </details>
    </div>

    <div class="card">
      <div class="wl-card-head">
        <div class="card-title">Settimane ISO del bimestre</div>
        <div class="wl-card-sub">${weekStartsSorted.length} settimane lun–dom · ore (giorni lavorati)</div>
      </div>
      <div class="table-wrapper">
        <table class="workload-table wl-sticky-first">
            <thead>
              <tr>
                <th>Settimana</th>
                ${doctors.map(d => `<th class="num">${escapeHtmlText(d.sigla)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${weekStartsSorted.map(weekStart => {
                const hoursByDoc = weeklyBreakdown[weekStart] || {};
                const [, mm, dd] = weekStart.split('-');
                const isoWeek = getISOWeekString(weekStart);
                const label = `${dd}/${mm} <span class="wl-sub">${isoWeek.slice(5)}</span>`;
                return `
                  <tr>
                    <td class="wl-doc" title="Lun ${weekStart} – dom ${getWeekEndForRange(weekStart)}">${label}</td>
                    ${doctors.map(doc => {
                      const w = hoursByDoc[doc.id] || {};
                      const h = w.total ?? 0;
                      const days = w.days ?? 0;
                      const isGap = Boolean(weeksGaps.byDoctor[doc.id]?.includes(weekStart));
                      const isPartial = !isGap && Boolean(weeksGaps.partialByDoctor[doc.id]?.includes(weekStart));
                      const cls = isGap ? 'negative' : (h > 40 ? 'over' : '');
                      const rep = w.reperibilita ?? 0;
                      const title = isGap
                        ? 'Nessun turno e nessuna assenza registrata: dato probabilmente incompleto'
                        : isPartial
                          ? 'Meno della metà del monte orario, senza assenze registrate: possibile assenza non inserita'
                          : (rep > 0 ? `Rep.: ${fmtHours(rep)}h (non in monte orario)` : 'Monte orario (turni + accreditate)');
                      const cellText = days > 0 ? `${fmtHours(h)}h <span class="wl-sub">(${days})</span>` : `${fmtHours(h)}h`;
                      const marker = isGap ? '⚠ ' : (isPartial ? '? ' : '');
                      return `<td class="num ${cls}" title="${title}">${marker}${cellText}</td>`;
                    }).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
        <div class="wl-legend">
          <span><span class="negative">⚠</span> nessun turno né assenza</span>
          <span>? sotto metà monte orario</span>
          <span><span class="over">oltre 40h</span></span>
        </div>
        ${renderGapsSummaryHtml(weeksGaps, doctors, 'nel periodo')}
        <details class="wl-note">
          <summary>Come si leggono le settimane</summary>
          <p>Ogni riga è una settimana ISO intera (lun–dom) che interseca il bimestre, anche se solo parte dei giorni ci ricade.</p>
          <p>Ore = turni assegnati (M/P 6h, Pr 7,5h, N 12h) + assenze accreditate; tra parentesi i giorni lavorati. La reperibilità non entra nel monte orario né nei giorni.</p>
          <p>Notte della domenica (20-8): 4h e 1 giorno nella settimana corrente, 8h e 1 giorno in quella successiva.</p>
        </details>
    </div>

    <div class="card">
      <div class="wl-card-head">
        <div class="card-title">Totale anno</div>
        <div class="wl-picker">
          <div class="wl-picker-fields">
            <div>
              <label for="workload-annual-year">Anno</label>
              <select id="workload-annual-year" class="year-select" title="Anno per il totale annuale">
                ${yearOpts.map(yr => `<option value="${yr}" ${yr === annualYear ? 'selected' : ''}>${yr}</option>`).join('')}
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="table-wrapper">
        <table class="workload-table wl-sticky-first">
            <thead>
              <tr>
                <th>Medico</th>
                <th class="num">Feriali</th>
                <th class="num">Weekend</th>
                <th class="num">Notturne</th>
                <th class="num">Rep.</th>
                <th class="num">Totale</th>
                <th class="num">Bilancio</th>
              </tr>
            </thead>
            <tbody>
              ${(() => {
                const { workload, expectedHoursByDoctor } = computeAnnualWorkloadFromAssignments(annualYear);
                return doctors.map(d => {
                  const w = workload[d.id] || { feriali: 0, weekend: 0, notturne: 0, reperibilita: 0, total: 0 };
                  const expected = expectedHoursByDoctor[d.id] ?? 0;
                  const diff = w.total - expected;
                  const fuoriBilancio = isExcludedFromWorkloadBalance(d);
                  return `
                    <tr>
                      <td class="wl-doc"><strong>${escapeHtmlText(d.sigla)}</strong> ${escapeHtmlText(d.cognome)}</td>
                      <td class="num">${fmtHours(w.feriali)}h</td>
                      <td class="num">${fmtHours(w.weekend)}h</td>
                      <td class="num">${fmtHours(w.notturne)}h</td>
                      <td class="num">${fmtHours(w.reperibilita)}h</td>
                      <td class="num">${fmtHours(w.total)}h</td>
                      <td class="num">
                        ${fuoriBilancio
                          ? '<span class="wl-sub" title="Direttore: solo turni Direzione, fuori dal bilanciamento del monte orario.">—</span>'
                          : balanceBadgeHtml(diff, `Ore effettive (turni + assenze accreditate) − ore dovute nelle sole settimane archiviate. 1 slot ≈ ${HOURS_PER_SLOT}h.`)}
                      </td>
                    </tr>
                  `;
                }).join('');
              })()}
            </tbody>
          </table>
        </div>
        <details class="wl-note">
          <summary>Come si calcola il totale anno</summary>
          <p>Contano <strong>solo le settimane archiviate</strong> (lucchetto) dell'anno selezionato; le altre valgono 0. Lo storico si aggiorna da solo quando nel <strong>Piano turni</strong> archivi o sblocchi una settimana.</p>
          <p>Il totale è calcolato una volta per settimana sul calendario corrente, per evitare doppi conteggi tra mesi.</p>
          <p>Bilancio = ore effettive (turni + assenze accreditate) − ore dovute delle settimane con dati (lun–ven non festivi × debito settimanale ÷ 5).</p>
        </details>
    </div>
  </div>
  `;

  const goToBimester = (target) => {
    if (!target) return;
    setWorkloadBimester(target.key);
    renderWorkloadView(container);
  };

  const bimesterSelect = container.querySelector('#workload-bimester');
  const bimesterYearSelect = container.querySelector('#workload-bimester-year');
  const onPickerChange = () => {
    const idx = parseInt(bimesterSelect?.value, 10);
    const yr = parseInt(bimesterYearSelect?.value, 10);
    if (Number.isNaN(idx) || Number.isNaN(yr)) return;
    goToBimester(getBimesterRange(yr, idx));
  };
  bimesterSelect?.addEventListener('change', onPickerChange);
  bimesterYearSelect?.addEventListener('change', onPickerChange);

  container.querySelector('[data-action="bimester-prev"]')?.addEventListener('click', () => {
    goToBimester(getAdjacentBimester(range.year, range.index, -1));
  });
  container.querySelector('[data-action="bimester-next"]')?.addEventListener('click', () => {
    goToBimester(getAdjacentBimester(range.year, range.index, 1));
  });
  container.querySelector('[data-action="bimester-today"]')?.addEventListener('click', () => {
    goToBimester(todayBimester);
  });

  container.querySelector('[data-action="bimester-pdf"]')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    btn.disabled = true;
    try {
      const res = await exportBimesterWorkloadPdf({ year: range.year, index: range.index, doctors: balanceDoctors });
      if (!res.ok) alert(res.error || 'Esportazione non riuscita');
    } finally {
      btn.disabled = false;
    }
  });

  container.querySelector('[data-action="bimester-consolidate"]')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    const label = bimesterSnapshot
      ? `Aggiornare il consolidamento di ${range.label}? I saldi registrati verranno sostituiti con quelli attuali.`
      : `Consolidare ${range.label}? I saldi attuali verranno congelati come dato di chiusura.`;
    // Il consolidamento regge le ritenute: se ci sono buchi, va detto prima e non dopo.
    const gapSiglas = balanceDoctors.filter(d => bimesterGaps.byDoctor[d.id]?.length).map(d => d.sigla);
    const partialSiglas = balanceDoctors.filter(d => bimesterGaps.partialByDoctor?.[d.id]?.length).map(d => d.sigla);
    let warn = '';
    if (gapSiglas.length) {
      warn += `\n\nATTENZIONE: ${gapSiglas.join(', ')} ${gapSiglas.length === 1 ? 'ha' : 'hanno'} settimane senza turni né assenze registrate. `
        + `Le loro ore dovute risultano più alte del reale e il saldo consolidato sarebbe sbagliato.`;
    }
    if (partialSiglas.length) {
      warn += `\n\nDa verificare: ${partialSiglas.join(', ')} ${partialSiglas.length === 1 ? 'ha' : 'hanno'} settimane sotto la metà del monte orario senza assenze registrate.`;
    }
    if (warn) warn += `\n\nConviene inserire le assenze mancanti prima di consolidare.`;
    if (!window.confirm(label + warn)) return;
    const snapshotRows = {};
    for (const d of balanceDoctors) {
      const m = bimesterMirror.byDoctor[d.id];
      if (!m) continue;
      snapshotRows[d.id] = {
        sigla: d.sigla,
        dovute: m.dovute,
        turni: m.turni,
        assenze: m.assenze,
        coperte: m.coperte,
        saldoApertura: m.saldoApertura,
        saldo: m.saldo,
        giorniFeriali: bimesterFerialiCount,
      };
    }
    btn.disabled = true;
    try {
      await saveBimesterSnapshot(bimesterKey, snapshotRows);
      renderWorkloadView(container);
    } catch (err) {
      btn.disabled = false;
      alert(`Consolidamento non riuscito: ${err?.message || err}`);
    }
  });

  container.querySelector('[data-action="bimester-unconsolidate"]')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget;
    if (!window.confirm(`Rimuovere il consolidamento di ${range.label}? Il dato di chiusura registrato verrà cancellato.`)) return;
    btn.disabled = true;
    try {
      await deleteBimesterSnapshot(bimesterKey);
      renderWorkloadView(container);
    } catch (err) {
      btn.disabled = false;
      alert(`Rimozione non riuscita: ${err?.message || err}`);
    }
  });

  container.querySelector('#workload-annual-year')?.addEventListener('change', (e) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v)) {
      setWorkloadAnnualYear(v);
      renderWorkloadView(container);
    }
  });
}
